import { Text } from '@app/components/core/typography/Text'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Style } from 'inlines'
import useMediaQuery from '@app/util/useMediaQuery'
import { Tag } from '@app/components/core/tag/Tag'
import { SectionHeader } from '../blocks/SectionHeader'
import { AppearItem, useAppearStyle } from '../blocks/AppearInViewport'

export type DataSectionItemStyling = 'text' | 'multi-line-text' | 'tag'
export type DataSectionItem = {
  label: string
  content: string
  styling?: DataSectionItemStyling
}
export type DataSectionType = {
  type: 'data'
  title: string
  listItems: DataSectionItem[]
}

export const DataContentSection = ({ title, listItems }: DataSectionType) => {
  const isMobile = useMediaQuery()
  const gap = isMobile ? '1rem' : '1.5rem'
  return (
    <Column gap={gap} alignItems="stretch">
      <AppearItem staggerIndex={0}>
        <SectionHeader title={title} />
      </AppearItem>
      {isMobile ? (
        <DataRows data={listItems} />
      ) : (
        <DataTable data={listItems} />
      )}
    </Column>
  )
}

const DataSectionContent = ({
  content,
  styling = 'text',
}: {
  content: string
  styling?: DataSectionItemStyling
}) => {
  switch (styling) {
    case 'multi-line-text':
      return (
        <Column gap="0.25rem">
          {content.split(',')?.map((line, index) => (
            <Text.Medium key={index}>{line.trim()}</Text.Medium>
          ))}
        </Column>
      )
    case 'tag':
      return (
        <Row gap="0.5rem" style={{ flexWrap: 'wrap' }}>
          {content.split(',')?.map((tag, index) => (
            <Tag
              key={index}
              variant="transparent"
              label={tag.trim()}
              size="small"
            />
          ))}
        </Row>
      )
    case 'text':
    default:
      return <Text.Medium>{content}</Text.Medium>
  }
}

const DataRows = ({ data }: { data: DataSectionItem[] }) => {
  return data?.map((dataItem, index) => (
    <AppearItem key={index} staggerIndex={index + 1}>
      <Column
        alignItems="stretch"
        gap="0.5rem"
        style={{
          paddingBlock: '1rem',
          borderBottom: '1px solid var(--gray-300)',
        }}
      >
        <Text.Medium color="#90919E">{dataItem.label}</Text.Medium>
        <DataSectionContent
          content={dataItem.content}
          styling={dataItem.styling}
        />
      </Column>
    </AppearItem>
  ))
}

const DataTableRow = ({
  dataItem,
  staggerIndex,
}: {
  dataItem: DataSectionItem
  staggerIndex: number
}) => {
  const appearStyle = useAppearStyle(staggerIndex)
  const cellStyle: Style = {
    paddingBlock: '1rem',
  }

  return (
    <tr style={{ borderBottom: '1px solid var(--gray-300)', ...appearStyle }}>
      <td style={{ ...cellStyle, width: '18.25rem', verticalAlign: 'top' }}>
        {<Text.Medium color="#90919E">{dataItem.label}</Text.Medium>}
      </td>
      <td style={{ ...cellStyle, paddingLeft: '1.5rem' }}>
        <DataSectionContent
          content={dataItem.content}
          styling={dataItem.styling}
        />
      </td>
    </tr>
  )
}

// TODO: Make it more universal and extract into an independent component
const DataTable = ({ data }: { data: DataSectionItem[] }) => {
  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
      }}
    >
      <tbody>
        {data?.map((dataItem, index) => (
          <DataTableRow
            key={index}
            dataItem={dataItem}
            staggerIndex={index + 1}
          />
        ))}
      </tbody>
    </table>
  )
}
